import React from 'react';

import classNames from 'classnames';

export type StatTone = 'blue' | 'violet' | 'emerald' | 'amber' | 'red' | 'sky' | 'gray';

interface StatCardProps {
    label: string;
    value: React.ReactNode;
    icon?: React.ComponentType<{ className?: string }>;
    tone?: StatTone;
    hint?: React.ReactNode;
    trend?: { value: string; direction: 'up' | 'down' | 'flat' };
    onClick?: () => void;
    className?: string;
}

const iconToneClasses: Record<StatTone, string> = {
    blue: 'bg-teal-50 text-teal-600 dark:bg-teal-900/30 dark:text-teal-400',
    violet: 'bg-cyan-50 text-cyan-600 dark:bg-cyan-900/30 dark:text-cyan-400',
    emerald: 'bg-emerald-50 text-emerald-600 dark:bg-emerald-900/30 dark:text-emerald-400',
    amber: 'bg-amber-50 text-amber-600 dark:bg-amber-900/30 dark:text-amber-400',
    red: 'bg-red-50 text-red-600 dark:bg-red-900/30 dark:text-red-400',
    sky: 'bg-sky-50 text-sky-600 dark:bg-sky-900/30 dark:text-sky-400',
    gray: 'bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-400',
};

const trendClasses = {
    up: 'text-emerald-600 dark:text-emerald-400',
    down: 'text-red-600 dark:text-red-400',
    flat: 'text-slate-500 dark:text-slate-400',
};

const trendArrows = { up: '↑', down: '↓', flat: '→' };

const StatCard: React.FC<StatCardProps> = ({ label, value, icon: Icon, tone = 'blue', hint, trend, onClick, className }) => {
    return (
        <div
            onClick={onClick}
            className={classNames(
                'bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl p-4 sm:p-5',
                { 'cursor-pointer transition-all duration-150 hover:border-slate-300 dark:hover:border-slate-700 hover:shadow-sm': !!onClick },
                className,
            )}
        >
            <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                    <p className="text-xs font-medium uppercase tracking-wide text-slate-500 dark:text-slate-400 truncate">{label}</p>
                    <p className="mt-1.5 text-2xl font-bold tracking-tight text-slate-900 dark:text-white">{value}</p>
                    {(trend || hint) && (
                        <div className="mt-1.5 flex items-center gap-2 text-xs">
                            {trend && (
                                <span className={classNames('font-medium', trendClasses[trend.direction])}>
                                    {trendArrows[trend.direction]} {trend.value}
                                </span>
                            )}
                            {hint && <span className="text-slate-500 dark:text-slate-400">{hint}</span>}
                        </div>
                    )}
                </div>
                {Icon && (
                    <div className={classNames('flex items-center justify-center w-10 h-10 rounded-lg shrink-0', iconToneClasses[tone])}>
                        <Icon className="w-5 h-5" />
                    </div>
                )}
            </div>
        </div>
    );
};

export default StatCard;
